import React, { useState,useEffect, useRef } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Typography,
  Collapse,
  Box,
  CssBaseline,
  Container,
  Card,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  Alert, Snackbar,
  Chip,
  Divider,
  useMediaQuery
} from "@mui/material";
import {
  CheckCircle,
  Info,
  CalendarMonth,
  Notifications,
  ExpandLess,
  ExpandMore
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Layout from "./Layout";
import { format, isValid } from "date-fns";
import { enGB } from "date-fns/locale";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { startOfToday, startOfTomorrow, startOfWeek, isWithinInterval,startOfDay,endOfDay, addDays } from "date-fns";
import { collection, query, where, getDocs,updateDoc,orderBy,onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { useAppContext } from "./AppContext";
import { keyframes } from '@emotion/react';

const flashAnimation = keyframes`
  0% { background-color: transparent; }
  50% { background-color: rgba(0, 255, 0, 0.1); }
  100% { background-color: transparent; }
`;

const NOTIFICATION_SOUND = '/notification.mp3';


const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Unfulfill");
  const [isOffline, setIsOffline] = useState(false); 
  const today = startOfToday();
  const tomorrow = startOfTomorrow();
  const startOfThisWeek = startOfWeek(today, { weekStartsOn: 1 });
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(addDays(fromDate, 6));
  const {shopId, t}=useAppContext();
  const [imageOpen,setImageOpen]=useState(false);
  const [selectedImage,setSelectedImage]=useState(null);
  const [isLoading,setIsLoading]=useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const audioRef = useRef(new Audio(NOTIFICATION_SOUND));
  const [newOrders, setNewOrders] = useState(new Set());
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const showTableView = !isMobile && !isTablet;

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOffline(!navigator.onLine);

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, "orders"), 
      where("shopId", "==", shopId),
      where("orderStatus", "not-in", ["Completed", "Canceled"]), 
      orderBy("fulfillmentTime", "asc")
    );
  
    // Set up a real-time listener with `onSnapshot`
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ordersData = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        orderDate: doc.data().orderDate.toDate(),
        fulfillmentDate: doc.data().fulfillmentDate.toDate(),
        fulfillmentTime: doc.data().fulfillmentTime.toDate(),
      }));
  
      setOrders(ordersData); // Update the state with the new orders data
      setIsLoading(false); // Turn off loading indicator after fetching data
    });
  
    // Cleanup the listener when component unmounts or dependencies change
    return () => unsubscribe();
  }, [shopId]);

  useEffect(() => {
    // Check for new orders when the orders prop changes
    const checkNewOrders = () => {
      const currentTime = new Date().getTime();
      const newOrderIds = orders
        .filter(order => {
          const orderTime =order.orderDate;
         // console.log(orderTime);
          return currentTime - orderTime < 300000; // Consider orders from last 5 minutes as new
        })
        .map(order => order.id);
      
        console.log(newOrderIds);
      if (newOrderIds.length > 0) {
        setNewOrders(new Set(newOrderIds));
        setSnackbarOpen(true);
        playNotification();
      }
    };

    checkNewOrders();
  }, [orders]);

  const playNotification = () => {
    try {
      audioRef.current.volume = 1.0; 
      audioRef.current.play();
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const isNewOrder = (orderId) => newOrders.has(orderId);
  
  const handleImageOpen = (image) => {
    setSelectedImage(image);
    setImageOpen(true);
  };

  const handleImageClose = () => {
    setImageOpen(false);
    setSelectedImage(null);
  };

  const filteredOrders = orders.filter((order) => {
    const orderDate = order.fulfillmentTime;

    if (fromDate && toDate && !isWithinInterval(orderDate, { start: fromDate, end: toDate })) {
      return false;
    }

    if (statusFilter === "Fulfill" && order.orderStatus !== "Fulfilled") {
      return false;
    }

    if (statusFilter === "Unfulfill" && order.orderStatus === "Fulfilled") {
      return false;
    }

    return true;
  });

  const todayOrders = orders.filter((order) => {
    const orderDate = order.fulfillmentTime;
    return orderDate >= startOfDay(today) && orderDate <= endOfDay(today);
  });

  const tomorrowOrders = orders.filter((order) => {
    const orderDate = order.fulfillmentTime;
    return orderDate >= startOfDay(tomorrow) && orderDate <= endOfDay(tomorrow);
  });

  const totalOrdersInWeek = orders.filter(
    (order) => order.fulfillmentTime >= startOfThisWeek
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOrderClick = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handleActionClick = (action, payload) => {
    if (action === "complete") {
      setConfirmOrderId(payload);
      setConfirmationOpen(true);
      console.log(confirmOrderId);
    } else if (action === "details") {
      if (!payload) {
        return;
      }
      handleOrderClick(payload);
    }
  };

  const handleConfirmationClose = async(confirmed) => {
    if (confirmed) {
      try {
       
        // Query the orders collection for the specific order and shopId
        const ordersQuery = query(
          collection(db, "orders"),
          where("shopId", "==", shopId),
          where("orderID", "==", confirmOrderId) 
        );
  
        const querySnapshot = await getDocs(ordersQuery);
  
        if (!querySnapshot.empty) {
          // Since we're querying by document ID, there should only be one result
          const orderDocRef = querySnapshot.docs[0].ref;
  
          // Update the order status to complete
          await updateDoc(orderDocRef, {
            orderStatus: "Fulfilled",
            completionDate: new Date(),
          });
  
          console.log(`Order ${confirmOrderId} for shop ${shopId} marked as complete.`);
           // Update the local state
            setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderID === confirmOrderId && order.shopId === shopId
                ? { ...order, orderStatus: "Fulfilled", completionDate: new Date() }
                : order
            )
          );
        } else {
          console.error(`Order ${confirmOrderId} for shop ${shopId} not found.`);
        }
  
      } catch (error) {
        console.error("Error updating order status: ", error);
      }
    }
    setConfirmOrderId(null);
    setConfirmationOpen(false);
  };

  const getStatusChipProps = (status) => {
    switch (status) {
      case "Fulfilled":
        return { color: "success", label: t("Fulfilled") };
      case "Unfulfilled":
        return { color: "warning", label: t("Unfulfilled") };
      default:
        return { color: "default", label: status ? t(status) : t("Unknown") };
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    if (isValid(date)) {
      return format(date, "dd-MM-yy");
    }
    return t("Invalid date");
  };

  const formatTime = (timestamp) => {
    const time = new Date(timestamp);
    if (isValid(time)) {
      return format(time, "h:mm a");
    }
    return t("Invalid date");
  };

  const renderExpandedDetails = (order) => (
    <Box
      sx={{
        margin: 1,
        padding: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          padding: 2,
          marginBottom: 2,
        }}
      >
        <CalendarMonth
          sx={{
            fontSize: 40,
            marginRight: 2,
            color: "#2196f3",
          }}
        />
        <Typography variant="h5" mr={3}>
          {t("Fulfillment Date")}
        </Typography>
        <Typography variant="h4">
          {formatDate(order.fulfillmentDate)} {formatTime(order.fulfillmentTime)}
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography fontWeight="bold">{t("Item Name")}</Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight="bold">{t("Variant")}</Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight="bold">{t("Quantity")}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {order.cartItems.map((item, index) => (
            <TableRow key={index}>
              <TableCell>{item.name}</TableCell>
              <TableCell>{item.selectedVariant}</TableCell>
              <TableCell>{item.quantity}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="subtitle1" sx={{ marginTop: 2 }} fontWeight="bold">
        {t("Inscription Text")}
      </Typography>
      <Typography variant="subtitle1">{order.inscription}</Typography>
      <Typography variant="subtitle1" sx={{ marginTop: 2 }} fontWeight="bold">
        {t("Order Notes")}
      </Typography>
      <Typography variant="subtitle1">{order.orderNotes}</Typography>
      <Typography variant="subtitle1" sx={{ marginTop: 1 }}>
        <strong>{t("Cashier")}:</strong> {order.cashierName}
      </Typography>
      <Box sx={{ marginTop: 2, cursor: "pointer" }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t("Images")}
        </Typography>
        {(order.orderImages && order.orderImages.length > 0)
          ? order.orderImages.map((image, index) => (
              <img
                key={index}
                src={image instanceof Blob || image instanceof File ? URL.createObjectURL(image) : image}
                alt={`Order attachment ${index + 1}`}
                style={{ borderRadius: 5 }}
                loading="lazy"
                width="200"
                height="200"
                onClick={() => handleImageOpen(image)}
              />
            ))
          : (order.imageUrls && order.imageUrls.length > 0)
            ? order.imageUrls.map((url, index) => (
                <img
                  key={index}
                  src={url}
                  alt={`Order URL image ${index + 1}`}
                  style={{ borderRadius: 5 }}
                  loading="lazy"
                  width="200"
                  height="200"
                  onClick={() => handleImageOpen(url)}
                />
              ))
            : <Typography variant="body2">{t("No images available")}</Typography>}
        <Dialog open={imageOpen} onClose={handleImageClose} maxWidth="md">
          <DialogContent>
            <img src={selectedImage} alt="Full size" style={{ width: "100%", height: "auto" }} />
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );

  return (
   
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Layout>
      <Container sx={{m:0}}>
        {isOffline && (
            <Alert severity="warning" sx={{ marginTop: '10px' }}>
              {t("You are offline. Some features may not be available.")}
            </Alert>
          )}
          <Typography variant="h4" mt={3}>
            {t("Manage Orders")}
          </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Box mt={3}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={4} md={4}>
                <Card variant="outlined" sx={{ padding: 2, textAlign: "left" }}>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={3}
                  >
                    
                    <Grid item>
                      <Typography variant="body" sx={{color:"#313131"}}>{t("Today Orders")}</Typography>
                      <Typography variant="h2">{todayOrders.length}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Card variant="outlined" sx={{ padding: 2, textAlign: "left" }}>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={3}
                  >
                    
                    <Grid item>
                      <Typography variant="body" sx={{color:"#313131"}}>
                        {t("Tomorrow Orders")}
                      </Typography>
                      <Typography variant="h2">{tomorrowOrders.length}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Card variant="outlined" sx={{ padding: 2, textAlign: "left" }}>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={3}
                  >
                   
                    <Grid item>
                      <Typography variant="body" sx={{color:"#313131"}}>
                        {t("Total Orders in Week")}
                      </Typography>
                      <Typography variant="h2">{totalOrdersInWeek.length}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>

            {/* Filters */}
            <Grid
              container
              spacing={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} sm={4} md={2}>
                <DatePicker
                  label={t("From")}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                  inputFormat="dd/MM/yyyy"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <DatePicker
                  label={t("To")}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item sx={{ paddingLeft: 2 }} xs={12} sm={4} md={8} display="flex" justifyContent="flex-end">
                <RadioGroup
                  row
                  aria-label={t("status-filter")}
                  name="status-filter"
                  autoFocus
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <FormControlLabel
                    value="All"
                    control={<Radio />}
                    label={t("All")}
                  />
                  <FormControlLabel
                    value="Fulfill"
                    control={<Radio />}
                    label={t("Fulfilled Orders")}
                  />
                  <FormControlLabel
                    value="Unfulfill"
                    control={<Radio />}
                    label={t("Unfulfilled Orders")}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
            
            
            {showTableView && (
            <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("Order ID")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("Inscription")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("Status")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("Fulfillment Date")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("Action")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {isLoading && (
                <CircularProgress sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} />
              )}
             
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order) => (
                      <React.Fragment key={order.id}>
                        <TableRow
                          onClick={() => handleOrderClick(order.id)}
                          sx={{
                            cursor: "pointer",
                            ...(isNewOrder(order.id) && {
                              animation: `${flashAnimation} 60s`,
                            }),
                          }}
                        >
                          <TableCell>{order.orderID}</TableCell>
                          <TableCell>{order.inscription}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              {...getStatusChipProps(order.orderStatus)}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(order.fulfillmentDate)} {formatTime(order.fulfillmentTime)}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleActionClick("complete", order.orderID);
                              }}
                              color="primary"
                            >
                              <CheckCircle/>
                            </IconButton>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleActionClick("details", order.id);
                              }}
                              color="secondary"
                            >
                              <Info/>
                            </IconButton>
                          
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 ,border:"none"}}
                            colSpan={5}
                          >
                            <Collapse
                              in={expandedOrderId === order.id}
                              timeout="auto"
                              unmountOnExit
                            >
                              {renderExpandedDetails(order)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            </>
            )}

            {!showTableView && (
              <Box sx={{ mt: 2 }}>
                {isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {filteredOrders
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((order) => (
                        <Grid item xs={12} key={order.id}>
                          <Card
                            sx={{
                              boxShadow: 2,
                              border: expandedOrderId === order.id ? `3px solid ${theme.palette.primary.main}` : "none",
                              transition: "all 0.3s ease",
                              animation: isNewOrder(order.id) ? `${flashAnimation} 60s` : "none",
                              '&:hover': {
                                boxShadow: 4,
                              },
                            }}
                          >
                            <CardContent
                              sx={{ pb: 1, cursor: "pointer" }}
                              onClick={() => handleOrderClick(order.id)}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
                                    {t("Order")} #{order.orderID}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    {order.inscription || t("No inscription")}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {t("Fulfillment")}: {formatDate(order.fulfillmentDate)} {formatTime(order.fulfillmentTime)}
                                  </Typography>
                                </Box>
                                <Chip
                                  {...getStatusChipProps(order.orderStatus)}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                  onClick={(event) => event.stopPropagation()}
                                />
                              </Box>

                              <Divider sx={{ my: 1.5 }} />

                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    {t("Customer")}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {order.customerName || t("Walk-in")}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    {t("Phone")}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {order.phoneNumber || t("N/A")}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    {t("Notes")}
                                  </Typography>
                                  <Typography variant="body2">
                                    {order.orderNotes || t("No notes")}
                                  </Typography>
                                </Grid>
                              </Grid>

                              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                                <IconButton
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOrderClick(order.id);
                                  }}
                                  size="small"
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  {expandedOrderId === order.id ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </Box>
                            </CardContent>

                            <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                              <Divider />
                              <CardContent sx={{ pt: 2 }}>
                                {renderExpandedDetails(order)}
                              </CardContent>
                            </Collapse>

                            <Divider />
                            <CardActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2, alignItems: "stretch" }}>
                              <Button
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleActionClick("complete", order.orderID);
                                }}
                                variant="contained"
                                color="primary"
                                fullWidth
                              >
                                {t("Complete")}
                              </Button>
                              <Button
                                size="small"
                                startIcon={<Info />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleActionClick("details", order.id);
                                }}
                                variant="outlined"
                                color="secondary"
                                fullWidth
                              >
                                {t("Details")}
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredOrders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </LocalizationProvider>
      </Container>
      </Layout>
      <ConfirmationDialog
        open={confirmationOpen}
        onClose={handleConfirmationClose}
        t={t}
      />
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="info" 
          sx={{ width: '100%' }}
          icon={<Notifications />}
        >
          {t("New orders have arrived!")}
        </Alert>
      </Snackbar>
    </ThemeProvider>
   
  );
};

const ConfirmationDialog = ({ open, onClose, t }) => {
  const handleConfirm = () => {
    onClose(true);
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{t("Action")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("Confirm order completion?")}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          variant="outline"
          color="primary"
          autoFocus
          disableElevation
        >
          {t("No")}
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disableElevation>
          {t("Yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageOrders;
